import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MotifRadioButtonGroup } from '../radio-button/radio-button-group/radio-button-group';
import { MotifRadioButton } from '../radio-button/radio-button';
import { FormFieldContext } from '../form-field/form-field.context';

let nextUniqueId;
nextUniqueId = 0;

const MIN_HOUR = 1;
const MIN_MINUTE = 0;
const MAX_HOUR = 12;
const MAX_MINUTE = 59;

const setZeroForOneDigit = digit => {
  if (digit.toString().length === 1) {
    return `0${digit}`;
  }

  return digit;
};

export const get24HrsFormat = (hoursTime, minutesTime, modifier) => {
  let hoursFormat24 = hoursTime;
  if (Number(hoursTime) === MAX_HOUR) {
    hoursFormat24 = '00';
  }

  if (modifier === 'PM') {
    hoursFormat24 = Math.round(hoursFormat24) + MAX_HOUR;
  }

  return hoursFormat24;
};

/**
 * @typedef Props
 * @prop {(x: Date) => void} [onChange]
 * @prop {Date} [value]
 * @prop {string} [className]
 * @prop {boolean} [disabled]
 * @prop {string} [id]
 * @prop {boolean} [required]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifTimeInput = (props) => {
  const {
    value = new Date(),
    onChange = null,
    disabled = false,
    id = '',
    required = false,
    className = '',
    ...other
  } = props;
  const hours = setZeroForOneDigit(value.getUTCHours());
  const minutes = setZeroForOneDigit(value.getUTCMinutes()) || '00';

  const AmOrPm = hours >= MAX_HOUR ? 'PM' : 'AM';
  const hoursFormat12 = hours % MAX_HOUR ? setZeroForOneDigit(hours % MAX_HOUR) : MAX_HOUR;

  const [valueHour, setValueHour] = useState(hoursFormat12);
  const [valueMinute, setValueMinute] = useState(minutes);
  const [timeModifier, setTimeModifier] = useState(AmOrPm);
  const hourRef = useRef(hoursFormat12);
  const minuteRef = useRef(minutes);

  const formFieldContext = useContext(FormFieldContext);

  useEffect(() => {
    formFieldContext.setRequired(required);
    formFieldContext.setDisabled(disabled);
    formFieldContext.setFormElementType('timeInput');
    id ? formFieldContext.setId(id) : formFieldContext.setId(`motif-time-input-${nextUniqueId++}`);
  }, []);

  const handleChangeHour = event => {
    const pattern = /^(1?[0-2]|0[1-9]|[0-9]|^$)$/;
    const match = pattern.test(event.target.value);

    if (match) {
      setValueHour(event.target.value);
    }
  };

  const handleChangeMinute = event => {
    const pattern = /^([1-5]?[0-9]|0[0-9]|^$)$/;
    const match = pattern.test(event.target.value);

    if (match) {
      setValueMinute(event.target.value);
    }
  };

  const handleBlurHour = () => {
    const hourValue = Number(valueHour) === 0 ? MAX_HOUR : setZeroForOneDigit(valueHour);

    const newDate = new Date(value);
    newDate.setUTCHours(get24HrsFormat(hourValue, valueMinute, timeModifier));

    if (String(hourValue) !== String(hourRef.current)) {
      hourRef.current = String(hourValue);
      onChange(newDate);
    }
    setValueHour(hourValue);
  };

  const handleBlurMinute = () => {
    const minuteValue = Number(valueMinute) === 0 ? MIN_MINUTE : setZeroForOneDigit(valueMinute);

    const newDate = new Date(value);
    newDate.setUTCMinutes(Number(minuteValue));

    if (String(minuteValue) !== String(minuteRef.current)) {
      minuteRef.current = String(minuteValue);
      onChange(newDate);
    }
    setValueMinute(setZeroForOneDigit(minuteValue));
  };

  const handleChangeAmOrPm = event => {
    const modifier = event.target.value;
    const newDate = new Date(value);
    newDate.setUTCHours(get24HrsFormat(valueHour, valueMinute, modifier));
    onChange(newDate);
    setTimeModifier(modifier);
  };

  const handleKeyDownHour = event => {
    const arrowDown = 40;
    const arrowUp = 38;

    // eslint-disable-next-line default-case
    switch (event.keyCode) {
      case arrowUp: {
        const hourUp = Number(valueHour) < MAX_HOUR ? Number(valueHour) + 1 : MIN_HOUR;
        const newDate = new Date(value);
        newDate.setUTCHours(get24HrsFormat(hourUp, valueMinute, timeModifier));
        onChange(newDate);
        setValueHour(setZeroForOneDigit(hourUp));
        hourRef.current = hourUp;
        break;
      }
      case arrowDown: {
        const hourDown = Number(valueHour) > MIN_HOUR ? Number(valueHour) - 1 : MAX_HOUR;
        const newDate = new Date(value);
        newDate.setUTCHours(get24HrsFormat(hourDown, valueMinute, timeModifier));
        onChange(newDate);
        setValueHour(setZeroForOneDigit(hourDown));
        hourRef.current = hourDown;
        break;
      }
    }
  };

  const handleKeyDownMinute = event => {
    const arrowDown = 40;
    const arrowUp = 38;

    // eslint-disable-next-line default-case
    switch (event.keyCode) {
      case arrowUp: {
        const minuteUp = Number(valueMinute) < MAX_MINUTE ? Number(valueMinute) + 1 : MIN_MINUTE;
        const newDate = new Date(value);
        newDate.setUTCMinutes(minuteUp);
        onChange(newDate);
        setValueMinute(setZeroForOneDigit(minuteUp));
        minuteRef.current = setZeroForOneDigit(minuteUp);
        break;
      }
      case arrowDown: {
        const minuteDown = Number(valueMinute) > MIN_MINUTE ? Number(valueMinute) - 1 : MAX_MINUTE;
        const newDate = new Date(value);
        newDate.setUTCMinutes(minuteDown);
        onChange(newDate);
        setValueMinute(setZeroForOneDigit(minuteDown));
        minuteRef.current = setZeroForOneDigit(minuteDown);
        break;
      }
    }
  };

  const classes = classNames({
    'motif-time-wrapper': true,
    [className]: true
  });

  const classesInput = classNames({
    'motif-small-input-item': true,
    'motif-small-input-item-invalid': formFieldContext.invalid
  });

  return (
    <div id={formFieldContext.id} className={classes} data-motif-time-input>
      <div className="motif-time-numbers">
        <input
          {...other}
          aria-label="Hour Input"
          className={classesInput}
          value={valueHour}
          onChange={handleChangeHour}
          onBlur={handleBlurHour}
          onKeyDown={handleKeyDownHour}
          disabled={disabled}
        />
      </div>
      <div className="motif-time-colon">:</div>
      <div className="motif-time-numbers">
        <input
          {...other}
          aria-label="Minute Input"
          className={classesInput}
          value={valueMinute}
          onChange={handleChangeMinute}
          onBlur={handleBlurMinute}
          onKeyDown={handleKeyDownMinute}
          disabled={disabled}
        />
      </div>
      <div className="motif-time-acronym">
        <MotifRadioButtonGroup disabled={disabled}>
          <MotifRadioButton
            className="motif-time-input-radio"
            value="AM"
            checked={timeModifier === 'AM'}
            onChange={handleChangeAmOrPm}
          >
            AM
          </MotifRadioButton>

          <MotifRadioButton
            className="motif-time-input-radio"
            value="PM"
            checked={timeModifier === 'PM'}
            onChange={handleChangeAmOrPm}
          >
            PM
          </MotifRadioButton>
        </MotifRadioButtonGroup>
      </div>
    </div>
  );
};

MotifTimeInput.propTypes = {
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  required: PropTypes.bool,
  className: PropTypes.string
};

export { MotifTimeInput };
